<script setup>
/**
 * @description 通用列表筛选组件，支持桌面端侧边栏和移动端抽屉模式
 * @param {Array} groups - 筛选组配置 [{id: string, title: string, type: 'accordion'|'checkbox-group', options: Array}]
 * @model v-model - 筛选数据对象
 * @model v-model:visible - 控制移动端抽屉显示
 * @emits reset - 当点击重置按钮时触发
 */

// 1. Imports
import { computed, ref, watch } from 'vue'
import { IconClose } from '@arco-design/web-vue/es/icon'
import BaseButton from '~/components/base/button/Button.vue'
// defineModel is a compiler macro, no import needed

const { withBase } = useBasePath()

// 2. Props
const props = defineProps({
  groups: {
    type: Array,
    default: () => [],
  },
  /** 移动端是否使用弹窗模式（弹窗在筛选栏下方，而非右侧抽屉） */
  mobilePopupMode: {
    type: Boolean,
    default: false,
  },
  /** 移动端弹窗位置 */
  mobilePlacement: {
    type: String,
    default: 'right',
    validator: (value) => ['right', 'bottom'].includes(value),
  },
  /**
   * @description 桌面端无卡片底（透明底、无圆角、无内边距），用于与页面灰底一体；配合侧栏 sticky 使用
   */
  plainDesktop: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 紧凑筛选样式：用于服务支持列表页，PC 侧栏与移动端底部弹窗保持一致
   */
  compact: {
    type: Boolean,
    default: false,
  },
})

// 3. Models & Emits
const modelValue = defineModel({
  type: Object,
  default: () => ({}),
})

const visible = defineModel('visible', {
  type: Boolean,
  default: false,
})

const emit = defineEmits(['reset'])

// 3.5 Local state for mobile (to support confirm before submit)
const localFilters = ref({})
const drawerSurfaceStyle = {
  borderRadius: '12px 12px 0 0',
  backgroundColor: 'var(--ip-bg-surface)',
}

/**
 * @description 仅保留包含可用选项的筛选组，避免展示没有数据的筛选条件
 * @returns {Array}
 */
const visibleGroups = computed(() => {
  return props.groups.filter((group) => Array.isArray(group?.options) && group.options.length > 0)
})

watch(visible, (newVisible) => {
  if (newVisible) {
    // 每次打开抽屉时，同步当前的 modelValue 到 localFilters
    const initialValues = JSON.parse(JSON.stringify(modelValue.value))
    // 确保所有 group.id 都在 localFilters 中，避免 v-model 绑定到 undefined
    visibleGroups.value.forEach((group) => {
      if (!initialValues[group.id]) {
        initialValues[group.id] = []
      }
    })
    localFilters.value = initialValues
  }
})

// 4. Computed
/**
 * @description 是否存在有效的筛选组
 * @returns {boolean}
 */
const hasFilters = computed(() => visibleGroups.value.length > 0)

/**
 * @description 是否存在已选中的筛选条件
 * @returns {boolean}
 */
const hasActiveFilters = computed(() => {
  return visibleGroups.value.some((group) => {
    const value = modelValue.value[group.id]
    if (Array.isArray(value)) {
      return value.length > 0
    }
    return !!value
  })
})

/**
 * @description 移动端本地是否存在已选中的筛选条件
 * @returns {boolean}
 */
const hasLocalActiveFilters = computed(() => {
  return visibleGroups.value.some((group) => {
    const value = localFilters.value[group.id]
    if (Array.isArray(value)) {
      return value.length > 0
    }
    return !!value
  })
})

// 5. Methods
/**
 * @description 切换选项选中状态
 * @param {string} groupId - 筛选组 ID
 * @param {any} value - 选项值
 * @param {boolean} isMobile - 是否为移动端操作
 */
const toggleOption = (groupId, value, isMobile = false) => {
  const target = isMobile ? localFilters.value : modelValue.value
  const currentValues = [...(target[groupId] || [])]
  const index = currentValues.indexOf(value)
  if (index > -1) {
    currentValues.splice(index, 1)
  } else {
    currentValues.push(value)
  }

  if (isMobile) {
    localFilters.value = {
      ...localFilters.value,
      [groupId]: currentValues,
    }
  } else {
    modelValue.value = {
      ...modelValue.value,
      [groupId]: currentValues,
    }
  }
}

/**
 * @description 重置所有筛选条件
 * @param {boolean} isMobile - 是否为移动端操作
 */
const handleReset = (isMobile = false) => {
  if (isMobile) {
    const resetValue = { ...localFilters.value }
    visibleGroups.value.forEach((group) => {
      resetValue[group.id] = []
    })
    localFilters.value = resetValue
  } else {
    const resetValue = { ...modelValue.value }
    visibleGroups.value.forEach((group) => {
      resetValue[group.id] = []
    })
    modelValue.value = resetValue
    emit('reset')
  }
}

/**
 * @description 移动端确认筛选
 */
const handleConfirm = () => {
  modelValue.value = JSON.parse(JSON.stringify(localFilters.value))
  visible.value = false
}
</script>

<template>
  <Transition name="filter-fade">
    <div v-if="hasFilters" class="base-filter" :class="{ 'base-filter--compact': compact }">
      <!-- 桌面端筛选显示 (MD以上) -->
      <div class="base-filter__desktop" :class="{ 'base-filter__desktop--plain': plainDesktop }">
        <div class="base-filter__header">
          <h2 class="base-filter__title">筛选</h2>
          <a-button
            v-if="hasActiveFilters"
            type="text"
            size="small"
            class="base-filter__reset"
            @click="handleReset()"
          >
            重置
          </a-button>
        </div>

        <div class="base-filter__content">
          <div v-for="group in visibleGroups" :key="group.id" class="base-filter__group">
            <div class="base-filter__group-header">
              <span class="base-filter__group-title">{{ group.title }}</span>
            </div>
            <div class="base-filter__options">
              <template v-if="group.type === 'accordion'">
                <BaseFilterAccordion v-model="modelValue[group.id]" :options="group.options" />
              </template>
              <template v-else-if="group.type === 'checkbox-group'">
                <div v-for="opt in group.options" :key="opt.value" class="base-filter__option">
                  <a-checkbox v-model="modelValue[group.id]" :value="opt.value">
                    {{ opt.label }}
                  </a-checkbox>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- 移动端抽屉 (MD以下通过 visible 触发，mobilePopupMode 时由父组件渲染弹窗) -->
      <a-drawer
        v-if="!mobilePopupMode"
        v-model:visible="visible"
        :placement="mobilePlacement"
        :footer="false"
        :header="false"
        :width="mobilePlacement === 'bottom' ? '100%' : '280px'"
        :height="mobilePlacement === 'bottom' ? 'calc(100dvh - 102px)' : '100%'"
        class="base-filter__drawer"
        :drawer-style="mobilePlacement === 'bottom' ? drawerSurfaceStyle : {}"
        :body-style="{ padding: 0 }"
        :closable="false"
      >
        <div
          class="base-filter__drawer-container"
          :class="{
            'base-filter__drawer-container--bottom': mobilePlacement === 'bottom',
            'base-filter__drawer-container--compact': compact,
          }"
        >
          <div class="base-filter__drawer-header">
            <div class="header-action header-action--left"></div>
            <div class="header-title">筛选</div>
            <div class="header-action header-action--right">
              <button class="action-btn action-btn--close" @click="visible = false">
                <icon-close />
              </button>
            </div>
          </div>

          <div class="base-filter__drawer-body">
            <div class="drawer-inner" :class="{ 'is-bottom': mobilePlacement === 'bottom' }">
              <div v-for="group in visibleGroups" :key="group.id" class="base-filter__group">
                <div class="base-filter__group-header">
                  <span class="base-filter__group-title">{{ group.title }}</span>
                  <a-button
                    v-if="hasLocalActiveFilters && mobilePlacement === 'right'"
                    type="text"
                    size="small"
                    class="base-filter__reset"
                    @click="handleReset(true)"
                  >
                    重置
                  </a-button>
                </div>
                <div class="base-filter__options">
                  <template v-if="group.type === 'accordion'">
                    <BaseFilterAccordion
                      v-model="localFilters[group.id]"
                      :options="group.options"
                    />
                  </template>
                  <template v-else-if="group.type === 'checkbox-group'">
                    <div class="base-filter__checkbox-list">
                      <div
                        v-for="opt in group.options"
                        :key="opt.value"
                        class="base-filter__option"
                        :class="{ 'is-active': localFilters[group.id]?.includes(opt.value) }"
                        @click="toggleOption(group.id, opt.value, true)"
                      >
                        {{ opt.label }}
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- 扩展内容插槽 (例如: 咨询方案) -->
              <div v-if="$slots.extra" class="drawer-extra">
                <slot name="extra" />
              </div>
            </div>
          </div>

          <div class="base-filter__drawer-footer">
            <div class="base-filter__footer">
              <BaseButton type="default" class="base-filter__reset-btn" @click="handleReset(true)">
                重置
              </BaseButton>
              <BaseButton class="base-filter__confirm-btn" @click="handleConfirm">
                {{ mobilePlacement === 'bottom' ? '确认' : '查看结果' }}
              </BaseButton>
            </div>
          </div>
        </div>
      </a-drawer>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
@mixin compact-filter-content {
  .base-filter__content {
    padding-bottom: 0;
  }

  .base-filter__group {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }

  .base-filter__group-header {
    height: 38px;
    margin-bottom: 0;
    padding: 8px;
    box-sizing: border-box;
  }

  .base-filter__group-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }

  .base-filter__options,
  :deep(.filter-accordion),
  .base-filter__checkbox-list {
    gap: 0;
  }

  .base-filter__option,
  :deep(.filter-accordion__item) {
    min-height: 38px;
    padding: 8px;
    box-sizing: border-box;
  }

  :deep(.filter-accordion__item) {
    align-items: flex-start;
  }

  :deep(.filter-accordion__sub-options) {
    gap: 0;
    margin-top: 0;
    padding-left: 24px;
  }

  :deep(.filter-accordion__label) {
    display: block;
    font-size: 14px;
    line-height: 22px;
    color: $text-title-color;
    white-space: normal;
    overflow: visible;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  :deep(.filter-accordion__icon) {
    width: 16px;
    height: 16px;
    margin-top: 3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: $text-subtitle-color;
  }

  :deep(.arco-checkbox) {
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 8px;
    padding: 0;
  }

  :deep(.arco-checkbox-label) {
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 0;
    line-height: 22px;
  }

  :deep(.arco-checkbox-icon) {
    width: 16px;
    height: 16px;
    margin-top: 3px;
  }
}

.base-filter {
  width: 100%;
  user-select: none;
  min-height: 0;

  &__desktop {
    @include respond-to-max('md') {
      display: none;
    }

    @include respond-to('md') {
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
      box-sizing: border-box;
      background-color: $bg-white;
      border-radius: $radius-md;
      padding: 16px;
    }

    &--plain {
      @include respond-to('md') {
        background-color: transparent;
        border-radius: 0;
        padding: 0;
      }
    }
  }

  &__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    line-height: 32px;
  }

  &__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    padding-bottom: 16px;

    @include respond-to('md') {
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
      }
    }
  }

  &__title {
    font-size: $font-size-card-title;
    font-weight: 700;
    color: $text-title-color;
  }

  &__reset {
    padding: 0;
    margin-left: 4px;
    color: $primary-color;

    :deep(.arco-btn) {
      color: $primary-color;
    }

    &--disabled {
      color: $text-subtitle-color;
      cursor: not-allowed;

      :deep(.arco-btn) {
        color: $text-subtitle-color;
      }
    }

    &:hover:not(&--disabled) {
      background: transparent !important;
      opacity: 0.8;
    }
  }

  &__group {
    margin-bottom: 16px;
    border-bottom: 1px solid $border-dashed-color;
    padding-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &__group-header {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__group-title {
    font-size: $font-size-card-subtitle;
    font-weight: 700;
    color: $text-title-color;
    line-height: 24px;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__checkbox-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    :deep(.arco-checkbox) {
      display: inline-flex;
      align-items: center;

      .arco-checkbox-icon {
        flex-shrink: 0;
      }
    }
  }

  &__drawer-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;

    &--bottom {
      .base-filter__drawer-header {
        height: 58px;
      }

      .base-filter__drawer-footer {
        height: 68px;
      }

      .base-filter__drawer-body {
        padding: 0 16px 8px;
      }
    }
  }

  &__drawer-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    position: relative;
    padding: 0 16px;

    .header-title {
      text-align: center;
      font-size: 18px;
      font-weight: 600;
      color: $text-title-color;
      @include text-ellipsis(1);
      padding: 0 60px;
      width: 100%;
    }

    .header-action {
      position: absolute;
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;

      &--left {
        left: 16px;
      }

      &--right {
        right: 16px;
      }

      .action-btn {
        background: none;
        border: none;
        padding: 0;
        font-size: 16px;
        cursor: pointer;
        outline: none;

        &--close {
          font-size: 20px;
          color: $text-title-color;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &:active {
          opacity: 0.7;
        }
      }
    }
  }

  &__drawer-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 24px 16px;

    .drawer-inner.is-bottom & {
      padding: 0 16px 8px;
    }
  }

  &__drawer-footer {
    flex-shrink: 0;
    height: 68px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    background-color: $bg-white;
    display: flex;
    align-items: center;
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 0 16px;
  }

  &__reset-btn,
  &__confirm-btn {
    flex: 1 1 0;
    min-width: 0;
    font-size: 17px;
    font-weight: 400;
    line-height: 44px;
    height: 44px;
  }

  &--compact {
    .base-filter__header {
      height: 38px;
      margin-bottom: 0;
      padding: 8px;
      line-height: 22px;
      box-sizing: border-box;
    }

    .base-filter__title {
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    }

    .base-filter__reset {
      height: 22px;
      font-size: 14px;
      line-height: 22px;
    }

    @include compact-filter-content;
  }

  &__drawer-container--compact {
    @include compact-filter-content;
  }
}

.drawer-inner {
  &.is-bottom {
    padding: 0;
  }

  .drawer-extra {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid $bg-color;
  }
}

.filter-fade-enter-active,
.filter-fade-leave-active {
  transition: opacity 0.4s ease;
}

.filter-fade-enter-from,
.filter-fade-leave-to {
  opacity: 0;
}

:deep(.base-filter__drawer) {
  .arco-drawer-content {
    height: 100%;
  }

  .arco-drawer-body {
    padding: 0;
    overflow: hidden;
  }
}
</style>
